import { Product } from "@/actions/schemas/products";
import { create } from "zustand";

export type ChosenVariant = {
  id: string;
  name: string;
  type: string;
};

export type ChosenVariants = {
  [typeId: string]: ChosenVariant;
};

type State = {
  product: Product;
  quantity: number;
  price: number;
  stock: number;
  id: string;
  chosenVariants: ChosenVariants;
};

type Actions = {
  setProduct: (product: Product) => void;
  setQuantity: (quantity: number) => void;
  setPrice: (price: number) => void;
  setStock: (stock: number) => void;
  setId: (id: string) => void;
  setChosenVariants: (typeId: string, chosenVariant: ChosenVariant) => void;
};

const defaultInitState: State = {
  product: {} as Product,
  quantity: 1,
  price: 0,
  stock: 0,
  chosenVariants: {},
  id: "",
};

const getMinPriceVariant = (variants: Product["variants"]) => {
  return variants.reduce(
    (acc, item) => (acc.price.sale > item.price.sale ? item : acc),
    variants[0]
  );
};

const setInitialChosenVariants = (product: Product) => {
  const minPriceVariant = getMinPriceVariant(product.variants || []);

  return (
    minPriceVariant?.chosenVariationOptions?.reduce((acc, item) => {
      return {
        ...acc,
        [item.id]: {
          ...item.option,
          type: item.name,
        },
      };
    }, {}) || ({} as ChosenVariants)
  );
};

const getCurrentVariantByChosenVariant = (
  product: Product,
  chosenVariants: ChosenVariants
) => {
  const chosenVariant = product.variants?.find((variant) =>
    variant.chosenVariationOptions.every((opt) => {
      const chosen = Object.values(chosenVariants).find(
        (chosen) => chosen.id === opt.option.id
      );
      return !!chosen;
    })
  );

  return chosenVariant || ({} as Product["variants"][0]);
};

export const getProductStore = (product: Product): State => {
  const currentVariant = getMinPriceVariant(product?.variants || []);
  return {
    quantity: 1,
    product,
    chosenVariants: setInitialChosenVariants(product),
    price: currentVariant.price.sale || 0,
    stock: currentVariant.stock?.current || 0,
    id: currentVariant.id,
  };
};

export const useProductStore = create<State & Actions>((set) => {
  const initState: State = defaultInitState;
  return {
    ...initState,
    setProduct: (product: Product) => {
      const currentVariant = getMinPriceVariant(product?.variants || []);

      return set({
        product,
        chosenVariants: setInitialChosenVariants(product),
        price: currentVariant?.price?.sale || 0,
        stock: currentVariant?.stock?.current || 0,
        id: currentVariant?.id,
      });
    },
    setQuantity: (quantity: number) => set({ quantity }),
    setPrice: (price: number) => set({ price }),
    setStock: (stock: number) => set({ stock }),
    setId: (id: string) => set({ id }),
    setChosenVariants: (typeId: string, chosenVariant: ChosenVariant) => {
      return set((state) => {
        const currentVariant = getCurrentVariantByChosenVariant(
          state.product,
          state.chosenVariants
        );

        return {
          chosenVariants: {
            ...state.chosenVariants,
            [typeId]: chosenVariant,
          },
          price: currentVariant?.price.sale || 0,
          stock: currentVariant?.stock?.current || 0,
        };
      });
    },
  };
});
